// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * System Prompt Management Component
 * 
 * Admin interface for managing AI system prompts:
 * - List all prompts
 * - Create/Edit/Delete prompts
 * - Activate prompts
 * - Test prompts with sample messages
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';

import systemPromptService from '../../services/systemPromptService';
import KnowledgeItemIdsField from '../common/KnowledgeItemIdsField';
import AlertDialog from '../common/AlertDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import PageSectionHeader from '../common/PageSectionHeader';

const SystemPromptManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testingPrompt, setTestingPrompt] = useState(null);
  
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const response = await systemPromptService.listPrompts(true);
      setPrompts(response.prompts || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load prompts:', err);
      setError('Failed to load system prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPrompt(null);
    setShowDialog(true);
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setShowDialog(true);
  };

  const handleDelete = (prompt) => {
    setConfirmDialog({
      open: true,
      title: 'Delete System Prompt',
      message: `Are you sure you want to delete "${prompt.name}"? This action cannot be undone.`,
      onConfirm: () => confirmDelete(prompt.id)
    });
  };

  const confirmDelete = async (promptId) => {
    try {
      await systemPromptService.deletePrompt(promptId);
      setAlertDialog({
        open: true,
        title: 'Success',
        message: 'System prompt deleted successfully',
        severity: 'success'
      });
      loadPrompts();
    } catch (err) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to delete system prompt',
        severity: 'error'
      });
    }
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleActivate = (prompt) => {
    setConfirmDialog({
      open: true,
      title: 'Activate System Prompt',
      message: `Activate "${prompt.name}"? This will deactivate all other prompts.`,
      onConfirm: () => confirmActivate(prompt.id)
    });
  };

  const confirmActivate = async (promptId) => {
    try {
      await systemPromptService.activatePrompt(promptId);
      setAlertDialog({
        open: true,
        title: 'Success',
        message: 'System prompt activated successfully',
        severity: 'success'
      });
      loadPrompts();
    } catch (err) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to activate system prompt',
        severity: 'error'
      });
    }
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleTest = (prompt) => {
    setTestingPrompt(prompt);
    setShowTestDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageSectionHeader
        icon={<PsychologyIcon />}
        title="Chat Prompts Settings"
        description="Manage AI system prompts for chat (separate from Voice Process and QA Prompts Settings)."
        actions={(
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadPrompts}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ bgcolor: '#13213c' }}
            >
              Create Prompt
            </Button>
          </Stack>
        )}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1, border: '2px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            <Chip label="Total" color="primary" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
            📁 {prompts.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total prompts
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, border: '2px solid #4caf50' }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            <Chip label="Active" color="success" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
            ✅ {prompts.filter(p => p.is_active).length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Currently active
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, border: '2px solid #2196f3' }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            <Chip label="Categories" color="info" size="small" sx={{ mr: 0.5, fontSize: '0.8rem' }} />
            📊 {new Set(prompts.map(p => p.category).filter(Boolean)).size}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Unique categories
          </Typography>
        </Paper>
      </Box>

      {/* Prompts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No system prompts found. Create one to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              prompts.map((prompt) => (
                <TableRow key={prompt.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {prompt.name}
                    </Typography>
                    {prompt.is_default && (
                      <Chip label="Default" size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={prompt.category || 'general'} 
                      size="small" 
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                      {prompt.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {prompt.is_active ? (
                      <Chip label="Active" color="success" size="small" icon={<CheckCircleIcon />} />
                    ) : (
                      <Chip label="Inactive" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(prompt.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Test Prompt">
                        <IconButton size="small" color="info" onClick={() => handleTest(prompt)}>
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      {!prompt.is_active && (
                        <Tooltip title="Activate">
                          <IconButton size="small" color="success" onClick={() => handleActivate(prompt)}>
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(prompt)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleDelete(prompt)}
                          disabled={prompt.is_active}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      {showDialog && (
        <PromptEditorDialog
          open={showDialog}
          prompt={editingPrompt}
          onClose={() => setShowDialog(false)}
          onSave={() => {
            setShowDialog(false);
            loadPrompts();
          }}
          setAlertDialog={setAlertDialog}
        />
      )}

      {/* Test Dialog */}
      {showTestDialog && testingPrompt && (
        <PromptTestDialog
          open={showTestDialog}
          prompt={testingPrompt}
          onClose={() => setShowTestDialog(false)}
          setAlertDialog={setAlertDialog}
        />
      )}

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </Box>
  );
};

// Prompt Editor Dialog Component
const PromptEditorDialog = ({ open, prompt, onClose, onSave, setAlertDialog }) => {
  const [formData, setFormData] = useState({
    name: '',
    prompt_text: '',
    description: '',
    category: 'general'
  });
  const [kbScope, setKbScope] = useState('all');
  const [kbIds, setKbIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (prompt) {
      setFormData({
        name: prompt.name || '',
        prompt_text: prompt.prompt_text || '',
        description: prompt.description || '',
        category: prompt.category || 'general'
      });
      const raw = prompt.knowledge_item_ids;
      if (raw == null) {
        setKbScope('all');
        setKbIds([]);
      } else if (Array.isArray(raw) && raw.length === 0) {
        setKbScope('none');
        setKbIds([]);
      } else {
        setKbScope('selected');
        setKbIds(Array.isArray(raw) ? [...raw] : []);
      }
    } else {
      setFormData({
        name: '',
        prompt_text: '',
        description: '',
        category: 'general'
      });
      setKbScope('all');
      setKbIds([]);
    }
    setErrors({});
  }, [prompt, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.prompt_text.trim()) newErrors.prompt_text = 'Prompt text is required';
    if (formData.prompt_text.trim().length < 10) newErrors.prompt_text = 'Prompt text must be at least 10 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      const submitData = {
        name: formData.name.trim(),
        prompt_text: formData.prompt_text.trim(),
        description: formData.description.trim() || null,
        category: formData.category,
        knowledge_item_ids:
          kbScope === 'all' ? null : kbScope === 'none' ? [] : kbIds,
      };

      if (prompt) {
        await systemPromptService.updatePrompt(prompt.id, submitData);
        setAlertDialog({
          open: true,
          title: 'Success',
          message: 'System prompt updated successfully',
          severity: 'success'
        });
      } else {
        await systemPromptService.createPrompt(submitData);
        setAlertDialog({
          open: true,
          title: 'Success',
          message: 'System prompt created successfully',
          severity: 'success'
        });
      }
      
      onSave();
    } catch (err) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to save system prompt',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {prompt ? 'Edit System Prompt' : 'Create System Prompt'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Prompt Name *"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              placeholder="e.g., Professional Assistant"
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => handleChange('category', e.target.value)}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="sales">Sales</MenuItem>
                <MenuItem value="support">Support</MenuItem>
                <MenuItem value="analysis">Analysis</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of this prompt's purpose"
            />

            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">Chat Knowledge (RAG)</FormLabel>
              <RadioGroup
                value={kbScope}
                onChange={(e) => setKbScope(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" />}
                  label="All team knowledge (default)"
                />
                <FormControlLabel
                  value="none"
                  control={<Radio size="small" />}
                  label="No Knowledge — disable chat RAG for users of this prompt"
                />
                <FormControlLabel
                  value="selected"
                  control={<Radio size="small" />}
                  label="Only selected articles"
                />
              </RadioGroup>
            </FormControl>

            {kbScope === 'selected' && (
              <KnowledgeItemIdsField
                value={kbIds}
                onChange={setKbIds}
                disabled={saving}
                helperText="Search or paste source_key from Knowledge Management. Requires KNOWLEDGE_READ."
              />
            )}

            <TextField
              fullWidth
              multiline
              rows={12}
              label="Prompt Text *"
              value={formData.prompt_text}
              onChange={(e) => handleChange('prompt_text', e.target.value)}
              error={!!errors.prompt_text}
              helperText={errors.prompt_text || 'Enter the system prompt that will guide AI behavior'}
              placeholder="You are a helpful AI assistant..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : (prompt ? 'Save Changes' : 'Create Prompt')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Prompt Test Dialog Component
const PromptTestDialog = ({ open, prompt, onClose, setAlertDialog }) => {
  const [testMessage, setTestMessage] = useState('');
  const [response, setResponse] = useState(null);
  const [testing, setTesting] = useState(false);

  const handleTest = async () => {
    if (!testMessage.trim()) {
      setAlertDialog({
        open: true,
        title: 'Warning',
        message: 'Please enter a test message',
        severity: 'warning'
      });
      return;
    }

    try {
      setTesting(true);
      const result = await systemPromptService.testPrompt(prompt.prompt_text, testMessage);
      setResponse(result);
    } catch (err) {
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to test prompt',
        severity: 'error'
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Test System Prompt: {prompt.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Test Message"
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Enter a message to test how AI responds with this prompt..."
          />

          <Button
            variant="contained"
            onClick={handleTest}
            disabled={testing || !testMessage.trim()}
            startIcon={testing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            fullWidth
          >
            {testing ? 'Testing...' : 'Test Prompt'}
          </Button>

          {response && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  AI Response:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {response.ai_response}
                  </Typography>
                </Paper>
                <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Processing Time: {response.processing_time?.toFixed(2)}s
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Model: {response.model_used}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemPromptManagement;
